// TopVideoPopupNotification.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';

import { LinkWithLanguage as Link } from '../component/LinkWithLanguage.jsx';
import Loading from '../component/Loading.jsx';
import StatefulImage from '../component/StatefulImage.jsx';

import UserAvatarWithStatus from '../container/UserAvatarWithStatus.js';

import { LIVESTREAM_TYPE_LIVE_BADGE } from '../resource/userStatusIndicatorConstants.js';
import {
  getIsPortrait,
  getIsOnMobileBySize,
} from '../resource/getUserAgent.js';
import {
  STATUS_IDLE,
  STATUS_OPENING,
  STATUS_CLOSING,
} from '../resource/popupNotificationConstants.js';
import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';

import { textColor } from '../style/variables.js';
import SlideInFromRight from '../style/SlideInFromRight.js';
import FadeOut from '../style/FadeOut.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { mobileAspectRatio } from '../style/media.js';

export const TopVideoPopupNotification = ({
  userId,
  notificationStatus,
  notificationLink,
  thumbnailUrl,
  notificationTitle,
  notificationTag,
  oembedType,
  notificationPosition,
  notificationExp,
}) => {
  const isPortrait = getIsPortrait();
  const isOnMobile = getIsOnMobileBySize();
  const avatarSize = isOnMobile && isPortrait ? 24 : 36;
  const circleSize = isOnMobile && isPortrait ? 26 : 40;
  const circleOffset = isOnMobile && isPortrait ? 1 : 2;
  const lineSize = isOnMobile && isPortrait ? 1 : 2;
  const hasUserId = !!userId;
  return (
    <StyledTopVideoNotification
      to={notificationLink}
      notificationStatus={notificationStatus}
      hasUserId={hasUserId}
      onClick={() => {
        trackMixpanel({
          type: EventTypes.NOTIFICATION_CLICKED,
          payload: {
            id: notificationTag, // use tag for record not object id
            type: oembedType,
            position: notificationPosition,
            link: notificationLink,
            exp: notificationExp,
            viewId: getViewId({
              pathname: window.location.pathname,
            }),
            buttonId: ButtonId.Notification.ButtonNotificationLivestream,
          },
        });
      }}
    >
      {hasUserId ? (
        <Avatar>
          <UserAvatarWithStatus
            id={userId}
            size={avatarSize}
            liveStreamType={LIVESTREAM_TYPE_LIVE_BADGE}
            livestreamSize={isOnMobile && isPortrait ? 10 : 14}
            circleOffset={circleOffset}
            circleLineWidth={lineSize}
            shouldShowLiveStream
            disableLink
          />
        </Avatar>
      ) : (
        <Thumbnail
          avatarSize={avatarSize}
          css={`
            width: ${circleSize}px;
            height: ${circleSize}px;
          `}
        >
          <StatefulImage>
            <Loading data-key="loading" />
            <Loading data-key="error" />
            <ThumbnailImage data-key="target" src={thumbnailUrl} />
          </StatefulImage>
        </Thumbnail>
      )}
      <Content>{notificationTitle}</Content>
    </StyledTopVideoNotification>
  );
};

TopVideoPopupNotification.propTypes = {
  userId: PropTypes.string,
  notificationStatus: PropTypes.string,
  notificationLink: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  notificationTitle: PropTypes.string,
  notificationTag: PropTypes.string,
  oembedType: PropTypes.string,
  notificationPosition: PropTypes.string,
  notificationExp: PropTypes.number,
};

TopVideoPopupNotification.defaultProps = {
  userId: null,
  notificationStatus: STATUS_IDLE,
  notificationLink: null,
  thumbnailUrl: null,
  notificationTitle: null,
  notificationTag: null,
  oembedType: null,
  notificationPosition: null,
  notificationExp: null,
};

const StyledTopVideoNotification = styled(
  ({ notificationStatus, hasUserId, ...restProps }) => <Link {...restProps} />
).attrs(({ hasUserId }) => ({
  style: {
    borderTopLeftRadius: `${hasUserId ? 100 : 8}px`,
    borderBottomLeftRadius: `${hasUserId ? 100 : 8}px`,
  },
}))`
  position: relative;
  max-width: calc(100vw - 16px);
  padding: 6px 16px 6px 4px;
  display: flex;
  align-items: center;
  background-color: rgba(25, 25, 25, 0.8);
  ${({ notificationStatus }) => {
    switch (notificationStatus) {
      case STATUS_OPENING: {
        return css`
          animation: 0.4s forwards ${SlideInFromRight};
        `;
      }
      case STATUS_CLOSING: {
        return css`
          animation: 0.4s forwards ${FadeOut};
        `;
      }
    }
  }}
  ${mobileAspectRatio`
    @media (orientation: portrait) {
      padding: 4px 16px 4px 4px;
    }
  `};
`;

const Avatar = styled.div`
  position: relative;
  flex: none;
  display: flex;
  align-items: center;
`;

const Thumbnail = styled.div.attrs(({ avatarSize }) => ({
  style: {
    width: `${avatarSize}px`,
    height: `${avatarSize}px`,
  },
}))`
  position: relative;
  flex: none;
  display: flex;
  align-items: center;
`;

const ThumbnailImage = styled.img`
  border-radius: 4px;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Content = styled.div`
  ${TextEllipsis};
  padding-left: 12px;
  flex: auto;
  color: ${textColor.white};
  font-size: 14px;
  ${mobileAspectRatio`
    @media (orientation: portrait) {
      padding-left: 6px;
      font-size: 12px;
    }
  `};
`;

export default withTranslation()(TopVideoPopupNotification);
